import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CadastroTrilhosReempregoPage.css";
import { cadastrarFichaTrilhosReemprego } from "../estaleiro.api";
import {
  rowHasAnyDataReemprego,
  validateCadastroTrilhosReemprego,
} from "./rules/cadastroTrilhosReempregoValidation";
import { listarOrigensCarregamentoTrilhosReemprego } from "../../bases-estaleiro/origem-carregamento-trilhos-reemprego/origemCarregamentoTrilhosReemprego.api";
import { listarPerfisTrilhoEstaleiro } from "../../bases-estaleiro/perfil-trilhos/perfilTrilhosEstaleiro.api";
import { listarClassesEstaleiro } from "../../bases-estaleiro/classes/classesEstaleiro.api";
import { listarMotivosDefeitoReempregoEstaleiro } from "../../bases-estaleiro/motivo-defeito-reemprego/motivoDefeitoReempregoEstaleiro.api";

const ROW_COUNT = 100;
const safeText = (value) => String(value ?? "").trim();
const isAtivo = (row) => Number(row?.ativo) === 1;
const isClasseSucata = (classe) => safeText(classe).toUpperCase() === "SUCATA";
const toUniqueSorted = (items) =>
  Array.from(new Set(items.filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "pt-BR", { sensitivity: "base" })
  );

const formatOrigem = (row) => {
  const cidade = safeText(row?.cidade);
  const uf = safeText(row?.uf);
  if (!cidade && !uf) return "";
  if (!cidade) return uf;
  if (!uf) return cidade;
  return `${cidade} - ${uf}`;
};

const formatClasse = (row) => {
  const joined = safeText(row?.classe_sub_classe);
  if (joined) return joined;
  const classe = safeText(row?.classe);
  const subClasse = safeText(row?.sub_classe);
  if (!classe) return "";
  return subClasse ? `${classe} - ${subClasse}` : classe;
};

const todayYMD = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const createRows = () =>
  Array.from({ length: ROW_COUNT }, (_, index) => ({
    id: index + 1,
    perfil: "",
    metragem: "",
    classe: "",
    motivoSucata: "",
  }));

const INITIAL_FORM = {
  data: todayYMD(),
  origemCarregamento: "",
  recebimentoGeracao: "",
  numeroNf: "",
  carreta: "",
  cavalo: "",
};

const RECEBIMENTO_GERACAO_OPTIONS = [
  { value: "", label: "Selecione" },
  { value: "Sim", label: "Sim" },
  { value: "Não", label: "Não" },
];

const toNumber = (value) => {
  const normalized = String(value || "").replace(",", ".").trim();
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const formatMeters = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));

const buildMetragemLines = (items = []) => {
  const safeItems = Array.isArray(items) ? items : [items];
  const filled = safeItems.map((item) => safeText(item)).filter(Boolean);
  return [...filled, ""];
};

function SmoothSelect({ value, onChange, options, placeholder = "Selecione", className = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const prevValueRef = useRef(value);

  const closeDropdown = () => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      triggerRef.current?.blur?.();
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    const prev = String(prevValueRef.current ?? "");
    const next = String(value ?? "");
    if (open && prev !== next) setOpen(false);
    prevValueRef.current = value;
  }, [value, open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={rootRef} className={`ctr-pick ${open ? "is-open" : ""} ${className}`.trim()}>
      <button
        type="button"
        className="ctr-pick-btn"
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className={`ctr-pick-label ${selected ? "" : "is-placeholder"}`}>{selected?.label || placeholder}</span>
      </button>
      {open ? (
        <div className="ctr-pick-menu" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              key={`${option.value}-${option.label}`}
              className={`ctr-pick-opt ${value === option.value ? "is-selected" : ""}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(option.value);
                closeDropdown();
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SearchablePick({ value, onChange, options, placeholder = "Digite para buscar", className = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const prevValueRef = useRef(value);

  const closeDropdown = () => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      inputRef.current?.blur?.();
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    const prev = String(prevValueRef.current ?? "");
    const next = String(value ?? "");
    if (open && prev !== next) closeDropdown();
    prevValueRef.current = value;
  }, [value, open]);

  const filteredOptions = useMemo(() => {
    const query = String(value || "").toUpperCase().trim();
    if (!query) return options;
    return options.filter((option) => String(option).toUpperCase().includes(query));
  }, [options, value]);

  return (
    <div ref={rootRef} className={`ctr-combo ${open ? "is-open" : ""} ${className}`.trim()}>
      <div className="ctr-combo-box">
        <input
          type="text"
          className="ctr-combo-input"
          ref={inputRef}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          className="ctr-combo-toggle"
          onClick={() => {
            if (!disabled) setOpen((prev) => !prev);
          }}
          aria-label="Abrir opções"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
        />
      </div>

      {open ? (
        <div className="ctr-combo-menu" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                type="button"
                key={option}
                className={`ctr-combo-opt ${value === option ? "is-selected" : ""}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(option);
                  closeDropdown();
                }}
              >
                {option}
              </button>
            ))
          ) : (
            <p className="ctr-combo-empty">Nenhuma opção encontrada</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function CadastroTrilhosReempregoPage({ onBack }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [rows, setRows] = useState(() => createRows());
  const [perfilOptions, setPerfilOptions] = useState([]);
  const [classeOptions, setClasseOptions] = useState([]);
  const [origemOptions, setOrigemOptions] = useState([]);
  const [motivoSucataOptions, setMotivoSucataOptions] = useState([]);
  const [basesLoading, setBasesLoading] = useState(true);
  const [basesError, setBasesError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationModalGroups, setValidationModalGroups] = useState([]);
  const [metragemModal, setMetragemModal] = useState({
    open: false,
    rowIndex: -1,
    lines: [""],
    error: "",
  });

  useEffect(() => {
    let isMounted = true;

    const carregarBases = async () => {
      setBasesLoading(true);
      setBasesError("");

      try {
        const [perfis, classes, origens, motivos] = await Promise.all([
          listarPerfisTrilhoEstaleiro({ limit: 1000, offset: 0 }),
          listarClassesEstaleiro({ limit: 1000, offset: 0 }),
          listarOrigensCarregamentoTrilhosReemprego({ limit: 1000, offset: 0 }),
          listarMotivosDefeitoReempregoEstaleiro({ limit: 1000, offset: 0 }),
        ]);

        if (!isMounted) return;

        setPerfilOptions(toUniqueSorted(perfis.filter(isAtivo).map((row) => safeText(row?.perfil_trilho))));
        setClasseOptions(toUniqueSorted(classes.filter(isAtivo).map(formatClasse)));
        setOrigemOptions(toUniqueSorted(origens.filter(isAtivo).map(formatOrigem)));
        setMotivoSucataOptions(toUniqueSorted(motivos.filter(isAtivo).map((row) => safeText(row?.motivo_defeito))));
      } catch (error) {
        if (!isMounted) return;

        setPerfilOptions([]);
        setClasseOptions([]);
        setOrigemOptions([]);
        setMotivoSucataOptions([]);
        setBasesError(`Não foi possível carregar as bases do cadastro (${String(error?.message || "erro desconhecido").trim()}).`);
      } finally {
        if (isMounted) setBasesLoading(false);
      }
    };

    carregarBases();
    return () => {
      isMounted = false;
    };
  }, []);

  const perfilSelectOptions = useMemo(
    () => [{ value: "", label: "Selecione" }, ...perfilOptions.map((option) => ({ value: option, label: option }))],
    [perfilOptions]
  );

  const classeSelectOptions = useMemo(
    () => [{ value: "", label: "Selecione" }, ...classeOptions.map((option) => ({ value: option, label: option }))],
    [classeOptions]
  );

  const motivoSucataSelectOptions = useMemo(
    () => [{ value: "", label: "Selecione" }, ...motivoSucataOptions.map((option) => ({ value: option, label: option }))],
    [motivoSucataOptions]
  );

  const totais = useMemo(() => {
    const totalPecas = rows.filter(
      (row) =>
        String(row.perfil || "").trim() ||
        String(row.metragem || "").trim() ||
        String(row.classe || "").trim() ||
        String(row.motivoSucata || "").trim()
    ).length;

    const totalMetragem = rows.reduce((acc, row) => acc + toNumber(row.metragem), 0);
    return { totalPecas, totalMetragem };
  }, [rows]);

  const onChangeForm = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue =
      type === "checkbox"
        ? checked
        : name === "carreta" || name === "cavalo"
        ? String(value ?? "").toUpperCase()
        : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const onChangeRow = (rowIndex, field, value) => {
    setRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;

        if (field === "classe") {
          return {
            ...row,
            classe: value,
            motivoSucata: isClasseSucata(value) ? row.motivoSucata : "",
          };
        }

        return { ...row, [field]: value };
      })
    );
  };

  const openMetragemModal = (rowIndex) => {
    const row = rows[rowIndex] || {};
    if (!safeText(row.perfil) || !safeText(row.classe)) {
      setSaveMessage("Selecione Perfil e Classe dos trilhos antes de preencher a metragem.");
      setSaveError(true);
      return;
    }

    setSaveMessage("");
    setSaveError(false);
    setMetragemModal({
      open: true,
      rowIndex,
      lines: buildMetragemLines([row.metragem]),
      error: "",
    });
  };

  const closeMetragemModal = () => {
    setMetragemModal({ open: false, rowIndex: -1, lines: [""], error: "" });
  };

  const updateMetragemLine = (lineIndex, nextValue) => {
    setMetragemModal((prev) => {
      const rawNext = prev.lines.map((line, index) => (index === lineIndex ? nextValue : line));
      return {
        ...prev,
        lines: buildMetragemLines(rawNext),
        error: "",
      };
    });
  };

  const removeMetragemLine = (lineIndex) => {
    setMetragemModal((prev) => {
      const nextLines = prev.lines.filter((_, index) => index !== lineIndex);
      return {
        ...prev,
        lines: buildMetragemLines(nextLines),
        error: "",
      };
    });
  };

  const applyMetragemBatch = () => {
    const rowIndex = Number(metragemModal.rowIndex);
    const baseRow = rows[rowIndex];
    if (!baseRow) return closeMetragemModal();

    const metragemLines = metragemModal.lines.map((line) => safeText(line)).filter(Boolean);
    if (!metragemLines.length) {
      setMetragemModal((prev) => ({
        ...prev,
        error: "Informe pelo menos uma metragem. Use uma linha para cada valor.",
      }));
      return;
    }

    const invalidLines = metragemLines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => toNumber(line) <= 0);

    if (invalidLines.length) {
      setMetragemModal((prev) => ({
        ...prev,
        error: `Há metragens inválidas nas linhas: ${invalidLines
          .map(({ index }) => index + 1)
          .join(", ")}.`,
      }));
      return;
    }

    const targetIndexes = metragemLines.map((_, offset) => rowIndex + offset);
    const overflow = targetIndexes[targetIndexes.length - 1] >= rows.length;
    if (overflow) {
      setMetragemModal((prev) => ({
        ...prev,
        error: "Não há linhas livres suficientes abaixo para distribuir todas as metragens.",
      }));
      return;
    }

    const occupied = targetIndexes
      .slice(1)
      .filter((targetIndex) => rowHasAnyDataReemprego(rows[targetIndex] || {}));

    if (occupied.length) {
      setMetragemModal((prev) => ({
        ...prev,
        error: `As linhas ${occupied
          .map((targetIndex) => (rows[targetIndex]?.id ?? targetIndex + 1))
          .join(", ")} já possuem dados. Limpe essas linhas ou escolha outra posição.`,
      }));
      return;
    }

    setRows((prev) =>
      prev.map((row, index) => {
        const offset = index - rowIndex;
        if (offset < 0 || offset >= metragemLines.length) return row;

        return {
          ...row,
          perfil: baseRow.perfil,
          classe: baseRow.classe,
          motivoSucata: isClasseSucata(baseRow.classe) ? baseRow.motivoSucata : "",
          metragem: metragemLines[offset],
        };
      })
    );

    closeMetragemModal();
  };

  const resetFicha = () => {
    setForm({ ...INITIAL_FORM, data: todayYMD() });
    setRows(createRows());
    setValidationModalGroups([]);
  };

  const onClear = () => {
    resetFicha();
    setSaveMessage("");
    setSaveError(false);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveMessage("");
    setSaveError(false);
    setValidationModalGroups([]);

    const validation = validateCadastroTrilhosReemprego({ form, rows });
    if (!validation.isValid) {
      setSaveMessage(validation.firstError);
      setSaveError(true);
      setValidationModalGroups(validation.modalGroups);
      setIsSaving(false);
      return;
    }

    try {
      const data = await cadastrarFichaTrilhosReemprego({ form, rows });
      const lote = data?.codigo_lote ? ` Lote: ${data.codigo_lote}.` : "";
      resetFicha();
      setSaveMessage(`Ficha salva no servidor.${lote}`);
      setSaveError(false);
    } catch (error) {
      const detail = String(error?.message || "erro ao salvar").trim();
      setSaveMessage(`Não foi possível salvar no servidor (${detail}).`);
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const onCloseValidationModal = () => {
    setValidationModalGroups([]);
  };

  return (
    <main className="ctr-page">
      <div className="ctr-root">
        <aside className="ctr-sidebar">
          <button type="button" className="ctr-back" onClick={onBack}>
            Menu Principal
          </button>
          <h2>Romaneio Operacional</h2>
          <p>Trilhos para reemprego</p>

          <section className="ctr-side-card">
            <h3>Questionamentos da Ficha</h3>
            <label className="ctr-field">
              <span>Data</span>
              <input type="date" name="data" value={form.data} onChange={onChangeForm} required />
            </label>

            <div className="ctr-field">
              <span>Origem do carregamento</span>
              <SearchablePick
                value={form.origemCarregamento}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, origemCarregamento: nextValue }))}
                placeholder="Digite para filtrar a origem"
                options={origemOptions}
                disabled={basesLoading}
              />
            </div>

            <div className="ctr-field">
              <span>Recebimento de geração</span>
              <SmoothSelect
                value={form.recebimentoGeracao}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, recebimentoGeracao: nextValue }))}
                placeholder="Selecione"
                options={RECEBIMENTO_GERACAO_OPTIONS}
              />
            </div>

            <label className="ctr-field">
              <span>Nº NF</span>
              <input type="text" name="numeroNf" value={form.numeroNf} onChange={onChangeForm} />
            </label>

            <div className="ctr-vehicle-group">
              <p>Veículo</p>
              <label className="ctr-field">
                <span>Carreta</span>
                <input type="text" name="carreta" value={form.carreta} onChange={onChangeForm} />
              </label>
              <label className="ctr-field">
                <span>Cavalo</span>
                <input type="text" name="cavalo" value={form.cavalo} onChange={onChangeForm} />
              </label>
            </div>
          </section>

          {basesError ? <p className="ctr-saved is-error">{basesError}</p> : null}
        </aside>

        <section className="ctr-content">
          <header className="ctr-header">
            <div className="ctr-header-copy">
              <h1>Recebimento de Trilhos de Reemprego</h1>
              <p className="ctr-subtitle">Controle de lançamento e consolidação automática.</p>
            </div>
          </header>

          <form id="ctr-form" className="ctr-card" onSubmit={onSubmit}>
            <section className="ctr-table-wrap">
              <div className="ctr-table-shell">
                <table className="ctr-table ctr-table--head">
                  <colgroup>
                    <col className="ctr-w-id" />
                    <col className="ctr-w-perfil" />
                    <col className="ctr-w-classe" />
                    <col className="ctr-w-metragem" />
                    <col className="ctr-w-motivo" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="ctr-col-id">Nº</th>
                      <th className="ctr-col-perfil">Perfil</th>
                      <th className="ctr-col-classe">Classe dos trilhos</th>
                      <th className="ctr-col-metragem">Metragem</th>
                      <th className="ctr-col-motivo">Motivo da sucata</th>
                    </tr>
                  </thead>
                </table>

                <div className="ctr-table-body-wrap">
                  <table className="ctr-table ctr-table--body">
                    <colgroup>
                      <col className="ctr-w-id" />
                      <col className="ctr-w-perfil" />
                      <col className="ctr-w-classe" />
                      <col className="ctr-w-metragem" />
                      <col className="ctr-w-motivo" />
                    </colgroup>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="ctr-row-id">{row.id}</td>
                          <td>
                            <SmoothSelect
                              value={row.perfil}
                              onChange={(nextValue) => onChangeRow(index, "perfil", nextValue)}
                              placeholder="Selecione"
                              className="ctr-pick--cell ctr-pick--light"
                              options={perfilSelectOptions}
                            />
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.classe}
                              onChange={(nextValue) => onChangeRow(index, "classe", nextValue)}
                              placeholder="Selecione"
                              className="ctr-pick--cell ctr-pick--light"
                              options={classeSelectOptions}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`ctr-meter-btn ${row.metragem ? "" : "is-placeholder"}`}
                              onClick={() => openMetragemModal(index)}
                              disabled={!safeText(row.perfil) || !safeText(row.classe)}
                              title={
                                !safeText(row.perfil) || !safeText(row.classe)
                                  ? "Selecione Perfil e Classe antes de preencher a metragem"
                                  : "Clique para informar uma ou mais metragens"
                              }
                            >
                              {row.metragem || "Clique para preencher"}
                            </button>
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.motivoSucata}
                              onChange={(nextValue) => onChangeRow(index, "motivoSucata", nextValue)}
                              placeholder={isClasseSucata(row.classe) ? "Selecione" : "Somente para sucata"}
                              className="ctr-pick--cell ctr-pick--light"
                              disabled={!isClasseSucata(row.classe)}
                              options={motivoSucataSelectOptions}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {saveMessage ? <p className={`ctr-saved ctr-saved--in-form ${saveError ? "is-error" : ""}`}>{saveMessage}</p> : null}
          </form>

          <footer className="ctr-actions">
            <div className="ctr-actions-totals">
              <p>
                Total de peças: <strong>{totais.totalPecas}</strong>
              </p>
              <p>
                Total de metragem: <strong>{formatMeters(totais.totalMetragem)}</strong>
              </p>
            </div>
            <div className="ctr-actions-buttons">
              <button type="button" className="ctr-btn ctr-btn--ghost" onClick={onClear}>
                Limpar
              </button>
              <button type="submit" className="ctr-btn ctr-btn--primary" form="ctr-form" disabled={isSaving}>
                {isSaving ? "Salvando..." : "Salvar ficha"}
              </button>
            </div>
          </footer>

          {validationModalGroups.length ? (
            <div
              className="ctr-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ctr-modal-title"
              onClick={onCloseValidationModal}
            >
              <div className="ctr-modal-card" onClick={(event) => event.stopPropagation()}>
                <div className="ctr-modal-head">
                  <div className="ctr-modal-alert">!</div>
                  <div className="ctr-modal-title-wrap">
                    <h3 id="ctr-modal-title">Pendências para salvar</h3>
                    <p>Corrija os itens abaixo antes de salvar a ficha.</p>
                  </div>
                  <button type="button" className="ctr-modal-close" onClick={onCloseValidationModal} aria-label="Fechar">
                    x
                  </button>
                </div>

                <p className="ctr-modal-count">
                  {validationModalGroups.reduce((acc, group) => acc + group.errors.length, 0)} pendência(s) encontrada(s)
                </p>

                <div className="ctr-modal-groups">
                  {validationModalGroups.map((group) => (
                    <section key={group.id} className="ctr-modal-group">
                      <h4>{group.title}</h4>
                      <ul className="ctr-modal-list">
                        {group.errors.map((item, index) => (
                          <li key={`${group.id}-${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>

                <div className="ctr-modal-actions">
                  <button type="button" className="ctr-btn ctr-btn--primary" onClick={onCloseValidationModal}>
                    Entendi
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {metragemModal.open ? (
            <div
              className="ctr-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ctr-meter-modal-title"
              onClick={closeMetragemModal}
            >
              <div
                className="ctr-modal-card ctr-meter-modal-card"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="ctr-meter-modal-head">
                  <div className="ctr-modal-head">
                    <div className="ctr-modal-alert">+</div>
                    <div className="ctr-modal-title-wrap">
                      <h3 id="ctr-meter-modal-title">Lançar metragens</h3>
                      <p>
                        Informe uma metragem por linha. O sistema vai repetir Perfil e Classe
                        nas linhas abaixo usando cada metragem individualmente.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ctr-modal-close"
                      onClick={closeMetragemModal}
                      aria-label="Fechar"
                    >
                      x
                    </button>
                  </div>
                </div>

                <div className="ctr-meter-modal-body">
                  <div className="ctr-meter-summary">
                    <div>
                      <strong>Perfil:</strong> {rows[metragemModal.rowIndex]?.perfil || "—"}
                    </div>
                    <div>
                      <strong>Classe:</strong> {rows[metragemModal.rowIndex]?.classe || "—"}
                    </div>
                    {isClasseSucata(rows[metragemModal.rowIndex]?.classe) ? (
                      <div>
                        <strong>Motivo da sucata:</strong>{" "}
                        {rows[metragemModal.rowIndex]?.motivoSucata || "—"}
                      </div>
                    ) : null}
                  </div>

                  <div className="ctr-meter-field">
                    <span>Metragens</span>
                    <div className="ctr-meter-lines">
                      {metragemModal.lines.map((lineValue, lineIndex) => {
                        const targetRowNumber = (rows[metragemModal.rowIndex]?.id || metragemModal.rowIndex + 1) + lineIndex;
                        return (
                          <div key={`metragem-line-${lineIndex}`} className="ctr-meter-line">
                            <div className="ctr-meter-line-meta">
                              <strong>Linha {targetRowNumber}</strong>
                              <span>{lineIndex === 0 ? "Linha atual" : "Nova linha abaixo"}</span>
                            </div>

                            <input
                              type="text"
                              className="ctr-meter-input"
                              value={lineValue}
                              onChange={(event) => updateMetragemLine(lineIndex, event.target.value)}
                              placeholder="Ex: 18,50"
                              autoFocus={lineIndex === 0}
                            />

                            <button
                              type="button"
                              className="ctr-meter-remove"
                              onClick={() => removeMetragemLine(lineIndex)}
                              disabled={metragemModal.lines.length === 1}
                              aria-label={`Remover linha de metragem ${lineIndex + 1}`}
                            >
                              -
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <p className="ctr-meter-help">
                    Cada linha preenchida vira uma linha na tabela, repetindo Perfil e Classe automaticamente.
                  </p>

                  {metragemModal.error ? (
                    <p className="ctr-meter-error">{metragemModal.error}</p>
                  ) : null}
                </div>

                <div className="ctr-modal-actions ctr-meter-modal-foot">
                  <button type="button" className="ctr-btn ctr-btn--ghost" onClick={closeMetragemModal}>
                    Cancelar
                  </button>
                  <button type="button" className="ctr-btn ctr-btn--primary" onClick={applyMetragemBatch}>
                    Aplicar metragens
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
